import { useMemo, useSyncExternalStore } from 'react'
import {
  getActiveLocale,
  getCopy,
  subscribeTranslations,
  translationsVersion,
  type CatalogNode,
  type CopyModule,
  type ResolvedCopy,
} from '@floorfile/i18n'

/**
 * `useCopy` for a design-system primitive — the same resolution, without
 * requiring an `<I18nProvider>` above it.
 *
 * ## Why not `useCopy` from `@floorfile/i18n`
 *
 * `useCopy` reads the locale out of React context and **throws** when there is
 * no provider. That is the right shape for a feature screen, which always has
 * one. It is the wrong shape here: these primitives are rendered by ~200 call
 * sites including this package's own jsdom suites, and making every one of them
 * mount a provider to render a `<QueryBoundary>` would be a large amount of
 * test scaffolding whose only purpose is to satisfy a lookup that has a
 * perfectly good provider-free source.
 *
 * That source is the registry: `setActiveLocale` is mirrored out of
 * `I18nProvider` precisely so `getCopy()` works in a mutation callback or a
 * `lib/` helper, and it bumps the same version counter that catalogue
 * registration does. Subscribing to it gives a primitive both halves — the
 * active locale and the arrival of its catalogue — with correct React
 * semantics and no context.
 *
 * **`PaginationControls` still requires a provider**, because it also calls
 * `useFormat()`, and a formatter needs the COMPANY time zone, which genuinely
 * only exists in context (owner decision, 2026-08-02). This hook does not
 * change that; it just stops every *other* primitive inheriting the constraint.
 */
export function useUiCopy<T extends CatalogNode>(module: CopyModule<T>): ResolvedCopy<T> {
  const locale = useSyncExternalStore(subscribeTranslations, getActiveLocale, getActiveLocale)
  const version = useSyncExternalStore(subscribeTranslations, translationsVersion, translationsVersion)
  // `version` is not read inside: it is the signal that a catalogue ARRIVED,
  // and `getCopy` reads the registry it changes. Dropping it, as the rule
  // suggests, leaves a screen in English after its French words land.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- cache key for an external registry
  return useMemo(() => getCopy(module, locale), [module, locale, version])
}
